import type { RouteRecordRaw } from 'vue-router'

import { AdokLayout as Layout } from '@/apps/adok/app/layout'
import { routeParam } from '@/apps/adok/shared/lib'

const LEGACY_BALANCE_TABS: Record<string, string> = {
  summary: '/balance/summary',
  withdrawn: '/balance/withdrawals',
  replenishment: '/balance/top-up'
}

/**
 * Paths this app no longer serves, redirected onto the routes that replaced them — for
 * bookmarks and for links inside mails already sent. **This list only shrinks.**
 *
 * `app/router.ts` spreads it after every live route array and before the catch-all, so a
 * live route always wins the match; keep it there. Nothing here renders: a record with
 * `redirect` is resolved before its `component` would be mounted, so the `Layout` above
 * only holds children.
 */
const legacyRedirects: RouteRecordRaw[] = [
  {
    path: '/dashboard',
    redirect: '/',
    meta: { hidden: true }
  },
  {
    path: '/traffic',
    redirect: '/traffic/spots',
    meta: { hidden: true }
  },
  {
    path: '/referral',
    redirect: '/referrals',
    meta: { hidden: true }
  },
  {
    path: '/advertising',
    component: Layout,
    redirect: '/advertising/rotation',
    meta: {
      title: 'Advertising'
    },
    children: [
      {
        path: '/advertising/flat-deals/new',
        redirect: '/advertising/deals/new',
        meta: { hidden: true }
      },
      {
        path: '/advertising/flat-deals/:dealId/edit',
        redirect: (to) =>
          `/advertising/deals/${routeParam(to.params.dealId)}/edit`,
        meta: { hidden: true }
      },
      {
        path: '/advertising/flat-deals/:pathMatch(.*)*',
        redirect: '/advertising/deals',
        meta: { hidden: true }
      },
      {
        path: '/advertising/mediakits/new',
        redirect: '/advertising/media-kits/new',
        meta: { hidden: true }
      },
      {
        path: '/advertising/mediakits/:mediaKitId/edit',
        redirect: (to) =>
          `/advertising/media-kits/${routeParam(to.params.mediaKitId)}/edit`,
        meta: { hidden: true }
      },
      {
        path: '/advertising/mediakits/:pathMatch(.*)*',
        redirect: '/advertising/media-kits',
        meta: { hidden: true }
      }
    ]
  },
  {
    path: '/extras',
    component: Layout,
    redirect: '/extras/anti_blocks',
    meta: {
      title: 'Extra tools'
    },
    children: [
      {
        path: '/extras/anti_blocks',
        name: 'anti_blocks',
        redirect: '/anti-adblock-domains',
        meta: {
          title: 'Anti Blocks'
        }
      },
      {
        path: '/extras/anti_blocks/webhooks',
        name: 'anti_blocks-webhooks',
        redirect: '/profile/api',
        meta: {
          title: 'Anti Blocks Webhooks & API',
          hidden: true
        }
      },
      {
        path: '/extras/anti_blocks/:pathMatch(.*)*',
        redirect: '/anti-adblock-domains',
        meta: { hidden: true }
      },
      {
        path: '/extras/ad-carantine/:pathMatch(.*)*',
        redirect: '/malware-blockers',
        meta: { hidden: true }
      },
      {
        path: '/extras/ad_alerts/:pathMatch(.*)*',
        redirect: '/malware-tasks',
        meta: { hidden: true }
      },
      {
        path: '/extras/ad_scans/:platformId/:spotId/:typeId/new',
        redirect: (to) => ({
          name: 'malware-scans-new-from-spot',
          params: { spotId: routeParam(to.params.spotId) },
          query: {
            platformId: routeParam(to.params.platformId),
            typeId: routeParam(to.params.typeId)
          }
        }),
        meta: { hidden: true }
      },
      {
        path: '/extras/ad_scans/new',
        redirect: '/malware-scans/new',
        meta: { hidden: true }
      },
      {
        path: '/extras/ad_scans/:pathMatch(.*)*',
        redirect: '/malware-scans',
        meta: { hidden: true }
      }
    ]
  },
  {
    // The only relative paths in this file: `'balance/:tab'` resolves against this
    // `/profile` parent, so the block cannot be flattened.
    path: '/profile',
    component: Layout,
    meta: { hidden: true },
    children: [
      {
        path: 'balance',
        redirect: '/balance/summary'
      },
      {
        path: 'balance/:tab',
        redirect: (to) =>
          LEGACY_BALANCE_TABS[routeParam(to.params.tab)] ?? '/balance/summary'
      },
      {
        path: 'balance/:tab/:id',
        redirect: (to) =>
          routeParam(to.params.tab) === 'withdrawn'
            ? `/balance/withdrawals/${routeParam(to.params.id)}/credentials`
            : (LEGACY_BALANCE_TABS[routeParam(to.params.tab)] ??
              '/balance/summary')
      },
      {
        path: 'balance/:tab/:token/approve',
        redirect: (to) => ({
          path: '/balance/withdrawals/approve',
          query: { token: routeParam(to.params.token) }
        })
      },
      {
        path: 'balance/:tab/:withdawalId/activate',
        redirect: (to) =>
          `/balance/withdrawals/${routeParam(to.params.withdawalId)}/activate`
      },
      {
        path: '/payment',
        redirect: '/balance/top-up'
      },
      {
        path: '/profile/:tab',
        redirect: '/profile'
      }
    ]
  }
]

export default legacyRedirects
